import { BaseRecord } from "@refinedev/core";

export enum PurchaseOrderStatus {
  DRAFT = "DRAFT",
  SUBMITTED = "SUBMITTED",
  VALIDATED = "VALIDATED",
  PARTIALLY_APPROVED = "PARTIALLY_APPROVED",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  SENT_TO_VENDOR = "SENT_TO_VENDOR",
  ACKNOWLEDGED = "ACKNOWLEDGED",
  IN_PROGRESS = "IN_PROGRESS",
  PARTIALLY_DELIVERED = "PARTIALLY_DELIVERED",
  DELIVERED = "DELIVERED",
  READY_FOR_INVOICING = "READY_FOR_INVOICING",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export enum PaymentTerms {
  NET_30 = "NET_30",
  NET_60 = "NET_60",
  PAYMENT_ON_DELIVERY = "PAYMENT_ON_DELIVERY",
  ADVANCE_PAYMENT = "ADVANCE_PAYMENT",
  NET_15 = "NET_15",
  NET_90 = "NET_90",
}

export enum Currency {
  ETB = "ETB",
  USD = "USD",
  EUR = "EUR",
  GBP = "GBP",
}

export enum ShippingMethod {
  SUPPLIER_DELIVERY = "SUPPLIER_DELIVERY",
  CUSTOMER_PICKUP = "CUSTOMER_PICKUP",
  THIRD_PARTY_SHIPPER = "THIRD_PARTY_SHIPPER",
}

export enum ShippingCarrierType {
  AIR = "AIR",
  SURFACE = "SURFACE",
  OCEAN = "OCEAN",
}

export enum ApprovalStepStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export interface PurchaseRequestRef {
  requestId?: string;
  requestNo?: string;
  linkedAt?: Date;
  linkedBy?: string;
}

export interface OrderItem {
  id: string;
  name: string;
  description?: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  totalPrice: number;
  specifications?: string;
  expectedDeliveryDate?: Date;
  sourceRequestItemId?: string;
  planRef?: {
    planId: string;
    itemId: string;
  };
  tags?: string[];
  deliveredQuantity?: number;
  actualDeliveryDate?: Date;
}

export interface Approval {
  order: number;
  roleSlug: string;
  stepName?: string;
  approvedBy?: string;
  approvedOn?: Date;
  remarks?: string;
  stepStatus: ApprovalStepStatus;
}

export interface ShippingInfo {
  method: ShippingMethod;
  carrierType?: ShippingCarrierType;
  carrierName?: string;
  trackingNumber?: string;
  shippingAddress?: string;
  shippingCost?: number;
  specialInstructions?: string;
}

export interface ExchangeRate {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  rateDate: Date;
  source?: string;
}

export interface VendorContact {
  vendorId: string;
  companyName: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  tin?: string;
}

export interface Attachment {
  fileId: string;
  originalName: string;
  mimeType: string;
  size: number;
  category?: string;
}

export interface BudgetValidation {
  isValid: boolean;
  message?: string;
  validatedBy?: string;
  validatedAt?: Date;
}

export interface PurchaseOrder extends BaseRecord {
  id: string;
  orderNo: string;
  templateId?: string;
  teamId: string;
  dateCreated: Date;
  orderDeadline: Date;
  expectedDeliveryDate?: Date;
  actualDeliveryDate?: Date;
  createdBy?: string;
  updatedBy?: string;
  orderedBy?: string;
  vendor: VendorContact;
  purchaseRequestRef?: PurchaseRequestRef;
  items: OrderItem[];
  subtotalAmount: number;
  taxAmount?: number;
  shippingAmount?: number;
  discountAmount?: number;
  totalAmount: number;
  currency: Currency;
  exchangeRate?: ExchangeRate;
  paymentTerms: PaymentTerms;
  shippingInfo: ShippingInfo;
  status: PurchaseOrderStatus;
  approvals?: Approval[];
  budgetValidation?: BudgetValidation;
  attachments?: Attachment[];
  notes?: string;
  termsAndConditions?: string;
  tags?: string[];
  approvalType?: string;
  steps?: Array<{
    order: number;
    roleSlug: string;
    stepName?: string;
  }>;
  externalWorkflow?: Record<string, any>;
  flowCaseId?: string;
  flowCaseNumber?: string;
  submittedBy?: string;
  createdAt: Date;
  updatedAt: Date;
  vendorAcknowledgedAt?: Date;
  vendorAcknowledgmentRef?: string;
}

export interface SubmitPurchaseOrderInput {
  orderId?: string;
  orderData?: CreatePurchaseOrderInput;
  fiscalYear: number;
  templateId?: string;
}

export interface CreatePurchaseOrderInput {
  templateId?: string;
  teamId: string;
  dateCreated: Date;
  orderDeadline: Date;
  expectedDeliveryDate?: Date;
  orderedBy?: string;
  vendor: VendorContact;
  purchaseRequestId?: string;
  items: OrderItem[];
  taxAmount?: number;
  shippingAmount?: number;
  discountAmount?: number;
  currency: Currency;
  exchangeRate?: ExchangeRate;
  paymentTerms: PaymentTerms;
  shippingInfo: ShippingInfo;
  attachments?: Attachment[];
  notes?: string;
  termsAndConditions?: string;
  tags?: string[];
}